import { useState, useEffect } from "react";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

const inputSx = {
  "& .MuiInputBase-root": {
    backgroundColor: "rgba(255,255,255,0.04)",
    borderRadius: "8px",
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.15)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.3)" },
  "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.45)" },
  "& .MuiInputBase-input": { color: "#fff", fontSize: "0.9rem" },
};

const compactInputSx = {
  "& .MuiInputBase-root": {
    backgroundColor: "rgba(255,255,255,0.04)",
    borderRadius: "8px",
    height: "36px",
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.15)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.28)" },
  "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.4)", fontSize: "17px" },
  "& .MuiInputBase-input": { color: "#fff", fontSize: "0.85rem", padding: "0 8px 0 2px" },
  "& .MuiInputAdornment-root": { marginRight: "2px" },
};

const popperSx = {
  "& .MuiPaper-root": {
    backgroundColor: "#1C232C",
    border: "1px solid rgba(255,255,255,0.12)",
    color: "#fff",
  },
  "& .MuiPickersDay-root": { color: "#e0e6f0" },
  "& .MuiPickersDay-root.Mui-selected": { backgroundColor: "#FDB940", color: "#0d1117" },
  "& .MuiPickersDay-root:hover": { backgroundColor: "rgba(253,185,64,0.15)" },
  "& .MuiDayCalendar-weekDayLabel": { color: "rgba(255,255,255,0.45)" },
  "& .MuiPickersCalendarHeader-label": { color: "#fff" },
  "& .MuiIconButton-root": { color: "rgba(255,255,255,0.6)" },
};

const fieldLabelSx = {
  fontSize: "0.68rem",
  fontWeight: 600,
  color: "rgba(255,255,255,0.45)",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  mb: "6px",
};

/**
 * Props:
 *   startDate – dayjs value
 *   endDate   – dayjs value
 *   onApply   – (start: dayjs, end: dayjs) => void
 *   compact   – renders slim inline date chips (no card wrapper, no Apply button)
 *   children  – rendered below (full mode only)
 */
export default function DarkDateRangePicker({ startDate, endDate, onApply, compact, children }) {
  const [tempStart, setTempStart] = useState(startDate ?? dayjs().subtract(1, "day"));
  const [tempEnd, setTempEnd] = useState(endDate ?? dayjs());

  useEffect(() => { if (startDate) setTempStart(startDate); }, [startDate]);
  useEffect(() => { if (endDate) setTempEnd(endDate); }, [endDate]);

  /* ── Compact mode: two inline chips, apply immediately on change ── */
  if (compact) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            format="DD MMM YYYY"
            value={tempStart}
            onChange={(v) => { if (v) { setTempStart(v); if (onApply) onApply(v, tempEnd); } }}
            sx={compactInputSx}
            slotProps={{ textField: { size: "small" }, popper: { sx: popperSx } }}
          />
        </LocalizationProvider>
        <Typography sx={{ color: "rgba(255,255,255,0.35)", fontSize: "0.9rem" }}>→</Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            format="DD MMM YYYY"
            value={tempEnd}
            onChange={(v) => { if (v) { setTempEnd(v); if (onApply) onApply(tempStart, v); } }}
            sx={compactInputSx}
            slotProps={{ textField: { size: "small" }, popper: { sx: popperSx } }}
          />
        </LocalizationProvider>
      </Box>
    );
  }

  /* ── Full mode (unchanged) ── */
  const handleApply = () => { if (onApply) onApply(tempStart, tempEnd); };

  return (
    <Box
      sx={{
        background: "#161B22",
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: "12px",
        padding: "20px 24px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: "12px", flexWrap: "wrap" }}>
        <Box>
          <Typography sx={fieldLabelSx}>Start Date</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format="YYYY/MM/DD"
              value={tempStart}
              onChange={(v) => v && setTempStart(v)}
              sx={inputSx}
              slotProps={{ textField: { size: "small" }, popper: { sx: popperSx } }}
            />
          </LocalizationProvider>
        </Box>
        <Box>
          <Typography sx={fieldLabelSx}>End Date</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format="YYYY/MM/DD"
              value={tempEnd}
              onChange={(v) => v && setTempEnd(v)}
              sx={inputSx}
              slotProps={{ textField: { size: "small" }, popper: { sx: popperSx } }}
            />
          </LocalizationProvider>
        </Box>
        <Button
          variant="contained"
          onClick={handleApply}
          sx={{
            backgroundColor: "#FDB940",
            color: "#0d1117",
            fontWeight: 700,
            fontSize: "0.85rem",
            textTransform: "none",
            borderRadius: "8px",
            px: "20px",
            height: "40px",
            boxShadow: "none",
            "&:hover": { backgroundColor: "#e5a730", boxShadow: "none" },
          }}
        >
          Apply range
        </Button>
      </Box>
      {children && <Box sx={{ mt: "14px" }}>{children}</Box>}
    </Box>
  );
}
